import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import {
  ArrowLeft, ClipboardCheck, Users, Mail, AlertTriangle, CheckCircle, XCircle,
  Clock, Building2, MapPin, Phone, Star, TrendingUp, Eye, Edit, Send, Ban,
  FileText, Calendar, User, RefreshCw, Brain, Target, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  "Pendente Aprovação": { color: "bg-amber-100 text-amber-800", icon: Clock },
  "Em Revisão": { color: "bg-blue-100 text-blue-800", icon: Eye },
  "Aprovada": { color: "bg-green-100 text-green-800", icon: CheckCircle },
  "Aprovada com Modificações": { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  "Rejeitada": { color: "bg-red-100 text-red-800", icon: XCircle },
  "Expirada": { color: "bg-gray-100 text-gray-800", icon: AlertTriangle }
};

export default function PropostaRevisaoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [proposta, setProposta] = useState(null);
  
  // Estado de modificações
  const [doadoresRemovidos, setDoadoresRemovidos] = useState([]);
  const [emailsEditados, setEmailsEditados] = useState({});
  const [observacoesGestor, setObservacoesGestor] = useState('');
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  
  // Modais
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [selectedEmailDoador, setSelectedEmailDoador] = useState(null);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);

  useEffect(() => {
    carregarProposta();
  }, [id]);

  const carregarProposta = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/propostas-doadores/${id}`);
      setProposta(res.data);
    } catch (error) {
      console.error('Erro ao carregar proposta:', error);
      toast.error('Erro ao carregar proposta');
      navigate('/doadores/propostas');
    } finally {
      setLoading(false);
    }
  };

  const toggleDoadorRemovido = (doadorId) => {
    setDoadoresRemovidos(prev => 
      prev.includes(doadorId) 
        ? prev.filter(id => id !== doadorId)
        : [...prev, doadorId]
    );
  };

  const handleEditEmail = (doadorId, field, value) => {
    setEmailsEditados(prev => ({
      ...prev,
      [doadorId]: {
        ...(prev[doadorId] || {}),
        [field]: value
      }
    }));
  };

  const handleAprovar = async () => {
    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('acao', 'APROVAR');
      formData.append('doadores_removidos', JSON.stringify(doadoresRemovidos));
      formData.append('emails_editados', JSON.stringify(
        Object.entries(emailsEditados).map(([doadorId, edits]) => ({
          doador_id: doadorId,
          ...edits
        }))
      ));
      formData.append('observacoes', observacoesGestor);
      formData.append('enviar_imediatamente', 'true');

      const res = await apiClient.post(`/propostas-doadores/${id}/aprovar`, formData);
      
      if (res.data.sucesso) {
        toast.success(
          res.data.envio?.enviados 
            ? `Aprovada! ${res.data.envio.enviados} emails enviados.`
            : 'Proposta aprovada com sucesso!'
        );
        navigate('/doadores/propostas');
      } else {
        toast.error(res.data.erro || 'Erro ao aprovar proposta');
      }
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      toast.error('Erro ao aprovar proposta');
    } finally {
      setSaving(false);
      setConfirmApproveOpen(false);
    }
  };

  const handleRejeitar = async () => {
    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('acao', 'REJEITAR');
      formData.append('motivo_rejeicao', motivoRejeicao);

      const res = await apiClient.post(`/propostas-doadores/${id}/aprovar`, formData);
      
      if (res.data.sucesso) {
        toast.success('Proposta rejeitada');
        navigate('/doadores/propostas');
      } else {
        toast.error(res.data.erro || 'Erro ao rejeitar proposta');
      }
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      toast.error('Erro ao rejeitar proposta');
    } finally {
      setSaving(false);
      setConfirmRejectOpen(false);
    }
  };

  const openEmailPreview = (doador) => {
    setSelectedEmailDoador(doador);
    setEmailPreviewOpen(true);
  };

  const getEmailForDoador = (doadorId) => {
    const editado = emailsEditados[doadorId];
    const original = proposta?.emails_preview?.find(e => e.doador_id === doadorId);
    return {
      assunto: editado?.assunto || original?.assunto || '',
      corpo_texto: editado?.corpo_texto || original?.corpo_texto || '',
      corpo_html: editado?.corpo_html || original?.corpo_html || ''
    };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-PT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const canEdit = proposta?.status === 'Pendente Aprovação' || proposta?.status === 'Em Revisão';
  const doadoresAtivos = (proposta?.doadores_sugeridos || []).filter(d => !doadoresRemovidos.includes(d.doador_id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!proposta) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
        <p className="text-lg font-medium">Proposta não encontrada</p>
        <Button onClick={() => navigate('/doadores/propostas')} className="mt-4">
          Voltar às Propostas
        </Button>
      </div>
    );
  }

  const StatusIcon = STATUS_CONFIG[proposta.status]?.icon || Clock;

  return (
    <div className="space-y-6" data-testid="proposta-revisao-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <Button variant="ghost" onClick={() => navigate('/doadores/propostas')} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <ClipboardCheck className="w-7 h-7 text-amber-500" />
            Revisão de Proposta
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <span className="font-mono text-slate-600 dark:text-slate-400">
              {proposta.codigo_proposta}
            </span>
            <Badge className={STATUS_CONFIG[proposta.status]?.color}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {proposta.status}
            </Badge>
          </div>
        </div>

        {canEdit && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setConfirmRejectOpen(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Rejeitar
            </Button>
            <Button 
              onClick={() => setConfirmApproveOpen(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Aprovar {doadoresRemovidos.length > 0 && 'com Modificações'}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs de Revisão */}
      <Tabs defaultValue="resumo" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="resumo">1. Resumo</TabsTrigger>
          <TabsTrigger value="necessidade">2. Necessidade</TabsTrigger>
          <TabsTrigger value="doadores">3. Doadores ({doadoresAtivos.length})</TabsTrigger>
          <TabsTrigger value="estrategia">4. Estratégia IA</TabsTrigger>
          <TabsTrigger value="emails">5. Emails Preview</TabsTrigger>
          <TabsTrigger value="decisao">6. Decisão</TabsTrigger>
        </TabsList>

        {/* Tab 1: Resumo */}
        <TabsContent value="resumo">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Informação da Proposta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Criada em</p>
                    <p className="font-medium">{formatDate(proposta.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Prazo Aprovação</p>
                    <p className="font-medium">{formatDate(proposta.prazo_aprovacao)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Doadores Sugeridos</p>
                    <p className="font-medium">{proposta.num_doadores_sugeridos}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Estratégia</p>
                    <Badge variant="outline">{proposta.estrategia_ia?.estrategia}</Badge>
                  </div>
                </div>
                
                {proposta.executada && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-slate-500">Execução</p>
                    <p className="font-medium text-green-600">
                      {proposta.contactos_enviados} emails enviados em {formatDate(proposta.data_execucao)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Necessidade Crítica
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Recurso</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {proposta.gap?.tipo_recurso_nome || 'N/A'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Quantidade</p>
                    <p className="font-medium">{proposta.gap?.quantidade_faltante || 0} unidades</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Beneficiários</p>
                    <p className="font-medium">{proposta.gap?.num_beneficiarios_afetados || 0} pessoas</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Criticidade</p>
                  <Badge className={
                    proposta.gap?.criticidade === 'Crítica' ? 'bg-red-500 text-white' :
                    proposta.gap?.criticidade === 'Alta' ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'
                  }>
                    {proposta.gap?.criticidade || 'Média'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab 2: Necessidade */}
        <TabsContent value="necessidade">
          <Card>
            <CardHeader>
              <CardTitle>Detalhes da Necessidade</CardTitle>
              <CardDescription>
                Informação completa sobre o gap identificado
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Tipo de Recurso</Label>
                    <p className="font-medium text-lg">{proposta.gap?.tipo_recurso_nome}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Código Recurso</Label>
                    <p className="font-mono">{proposta.gap?.tipo_recurso_codigo || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Quantidade em Falta</Label>
                    <p className="text-2xl font-bold text-red-600">{proposta.gap?.quantidade_faltante || 0}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Beneficiários Afetados</Label>
                    <p className="text-lg font-medium">{proposta.gap?.num_beneficiarios_afetados || 0} pessoas</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Distrito</Label>
                    <p className="font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {proposta.gap?.distrito_nome || 'Nacional'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Género / Faixa Etária</Label>
                    <p className="font-medium">
                      {proposta.gap?.genero || 'Todos'} • {proposta.gap?.faixa_etaria || 'Todas'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Doadores */}
        <TabsContent value="doadores">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Doadores Sugeridos ({doadoresAtivos.length} de {proposta.doadores_sugeridos?.length || 0})
              </CardTitle>
              <CardDescription>
                {canEdit 
                  ? 'Selecione os doadores a remover da lista de contacto'
                  : 'Lista de doadores sugeridos para contacto'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    {canEdit && <TableHead className="w-12">Incluir</TableHead>}
                    <TableHead>Doador</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead className="text-center">Score Match</TableHead>
                    <TableHead>Justificação IA</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(proposta.doadores_sugeridos || []).map((doador) => {
                    const isRemovido = doadoresRemovidos.includes(doador.doador_id);
                    
                    return (
                      <TableRow 
                        key={doador.doador_id}
                        className={isRemovido ? 'opacity-50 bg-red-50/50' : ''}
                      >
                        {canEdit && (
                          <TableCell>
                            <Checkbox
                              checked={!isRemovido}
                              onCheckedChange={() => toggleDoadorRemovido(doador.doador_id)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              doador.tipo_doador === 'Empresa' ? 'bg-blue-100' : 'bg-green-100'
                            }`}>
                              <User className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium">{doador.nome}</p>
                              <p className="text-xs text-slate-500">{doador.distrito_nome || 'Portugal'}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{doador.tipo_doador}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <p className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {doador.email}
                            </p>
                            {doador.telefone && (
                              <p className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {doador.telefone}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`text-2xl font-bold ${getScoreColor(doador.score_match)}`}>
                            {doador.score_match?.toFixed(0)}
                          </span>
                          <p className="text-xs text-slate-500">/ 100</p>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <p className="text-sm text-slate-600 truncate">
                            {doador.justificacao || doador.analise_ia?.justificacao || '-'}
                          </p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEmailPreview(doador)}
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Ver Email
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {doadoresRemovidos.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    {doadoresRemovidos.length} doador(es) removido(s) da lista de contacto
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4: Estratégia IA */}
        <TabsContent value="estrategia">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                Estratégia Recomendada pela IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Estratégia</Label>
                    <Badge className="text-lg px-4 py-1 bg-purple-100 text-purple-800">
                      {proposta.estrategia_ia?.estrategia || 'CONTACTO_DIRETO'}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-slate-500">Justificação</Label>
                    <p className="text-sm">{proposta.estrategia_ia?.justificacao || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-500">Nº Doadores a Contactar</Label>
                    <p className="text-2xl font-bold">{proposta.estrategia_ia?.num_contactar || doadoresAtivos.length}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Prioridade Telefone</Label>
                    <Badge variant="outline">
                      {proposta.estrategia_ia?.prioridade_telefone ? 'Sim' : 'Não'}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-slate-500">Criar Landing Page</Label>
                    <Badge variant="outline">
                      {proposta.estrategia_ia?.criar_landing_page ? 'Sim' : 'Não'}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 5: Emails Preview */}
        <TabsContent value="emails">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Preview de Emails ({proposta.emails_preview?.length || 0})
              </CardTitle>
              <CardDescription>
                {canEdit 
                  ? 'Reveja e edite os emails antes de aprovar'
                  : 'Emails gerados para os doadores'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {(proposta.emails_preview || []).map((email, idx) => {
                    const isRemovido = doadoresRemovidos.includes(email.doador_id);
                    const editado = emailsEditados[email.doador_id];
                    
                    return (
                      <Card 
                        key={email.doador_id} 
                        className={isRemovido ? 'opacity-50 border-red-200' : ''}
                      >
                        <CardHeader className="py-3">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-base flex items-center gap-2">
                              <User className="w-4 h-4" />
                              {email.doador_nome}
                              {editado && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">Editado</Badge>
                              )}
                            </CardTitle>
                            {canEdit && !isRemovido && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEmailPreview({
                                  doador_id: email.doador_id,
                                  nome: email.doador_nome
                                })}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Editar
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="py-2">
                          <div className="space-y-2">
                            <div>
                              <Label className="text-xs text-slate-500">Assunto</Label>
                              <p className="font-medium">{editado?.assunto || email.assunto}</p>
                            </div>
                            <Separator />
                            <div>
                              <Label className="text-xs text-slate-500">Corpo</Label>
                              <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded max-h-32 overflow-y-auto whitespace-pre-line">
                                {editado?.corpo_texto || email.corpo_texto}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 6: Decisão */}
        <TabsContent value="decisao">
          <div className="grid md:grid-cols-2 gap-6">
            {canEdit ? (
              <>
                <Card className="border-green-200">
                  <CardHeader>
                    <CardTitle className="text-green-700 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Aprovar Proposta
                    </CardTitle>
                    <CardDescription>
                      Os emails serão enviados aos {doadoresAtivos.length} doadores selecionados
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Observações do Gestor (opcional)</Label>
                      <Textarea
                        value={observacoesGestor}
                        onChange={e => setObservacoesGestor(e.target.value)}
                        placeholder="Adicione notas sobre a aprovação..."
                        rows={3}
                      />
                    </div>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => setConfirmApproveOpen(true)}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Aprovar e Enviar Emails
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-red-200">
                  <CardHeader>
                    <CardTitle className="text-red-700 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Rejeitar Proposta
                    </CardTitle>
                    <CardDescription>
                      Nenhum email será enviado
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Motivo da Rejeição *</Label>
                      <Textarea
                        value={motivoRejeicao}
                        onChange={e => setMotivoRejeicao(e.target.value)}
                        placeholder="Explique porque está a rejeitar esta proposta..."
                        rows={3}
                      />
                    </div>
                    <Button 
                      variant="outline"
                      className="w-full text-red-600 hover:bg-red-50"
                      onClick={() => setConfirmRejectOpen(true)}
                      disabled={!motivoRejeicao.trim()}
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Rejeitar Proposta
                    </Button>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Decisão Registada
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500">Decisão</Label>
                      <p className="font-medium">{proposta.decisao || proposta.status}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Data</Label>
                      <p className="font-medium">{formatDate(proposta.data_revisao)}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Revisor</Label>
                      <p className="font-medium">{proposta.revisado_por_nome || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500">Contactos Enviados</Label>
                      <p className="font-medium">{proposta.contactos_enviados || 0}</p>
                    </div>
                  </div>
                  {proposta.observacoes_gestor && (
                    <div className="pt-4 border-t">
                      <Label className="text-slate-500">Observações</Label>
                      <p>{proposta.observacoes_gestor}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal Editar Email */}
      <Dialog open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Email para: {selectedEmailDoador?.nome}
            </DialogTitle>
            <DialogDescription>
              {canEdit ? 'Edite o email antes de aprovar' : 'Preview do email'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmailDoador && (
            <div className="space-y-4">
              <div>
                <Label>Assunto</Label>
                <Input
                  value={getEmailForDoador(selectedEmailDoador.doador_id).assunto}
                  onChange={e => handleEditEmail(selectedEmailDoador.doador_id, 'assunto', e.target.value)}
                  disabled={!canEdit}
                />
              </div>
              <div>
                <Label>Corpo do Email</Label>
                <Textarea
                  value={getEmailForDoador(selectedEmailDoador.doador_id).corpo_texto}
                  onChange={e => handleEditEmail(selectedEmailDoador.doador_id, 'corpo_texto', e.target.value)}
                  rows={10}
                  disabled={!canEdit}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailPreviewOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Aprovação */}
      <AlertDialog open={confirmApproveOpen} onOpenChange={setConfirmApproveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              Confirmar Aprovação
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja aprovar esta proposta?
              <br /><br />
              <strong>{doadoresAtivos.length}</strong> emails serão enviados imediatamente aos doadores selecionados.
              {doadoresRemovidos.length > 0 && (
                <span className="block mt-2 text-amber-600">
                  ({doadoresRemovidos.length} doador(es) foram removidos da lista)
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleAprovar}
              className="bg-green-600 hover:bg-green-700"
              disabled={saving}
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Aprovar e Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Confirmar Rejeição */}
      <AlertDialog open={confirmRejectOpen} onOpenChange={setConfirmRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Confirmar Rejeição
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja rejeitar esta proposta?
              <br /><br />
              Nenhum email será enviado aos doadores. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRejeitar}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
              Confirmar Rejeição
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
